import '../../static/css/app.css';
import '../../static/css/studio-next.css';
import './fonts.css';
import './studio-parity.css';
import { DEFAULT_SETTINGS, OUTPUT_PRESETS, PROFILE_TRANSFER_FORMAT, PROFILE_TRANSFER_VERSION, exportProfileSettings, formatDuration, importProfileSettings, isVideoItem, moveItem, normalizeMediaItem, normalizeProject, parseScript, projectSlideCount, resolveExportSelection } from './project.js';
import { clearProject, getStorageEstimate, loadProject, requestPersistentStorage, saveProject } from './storage.js';
import { deleteCustomFont, listCustomFonts, saveCustomFont } from './storage.js';
import { deleteProfile, listProfiles, saveProfile } from './profile-sync.js';
import { STANDARD_FONTS, customFonts, ensureFontsLoaded, normalizeFontId, registerCustomFont, registerCustomFonts, unregisterCustomFont } from './font-registry.js';
import { ExportCancelledError, downloadBlob, exportMp4WithFonts as exportMp4, inspectExportSupport, renderFrame, renderThumbnail } from './renderer.js';

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];
const uid = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const ICONS = Object.freeze({
  copy: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="11" height="11" rx="2"></rect><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"></path></svg>',
  sparkle: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z"></path><path d="m19 15 .8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z"></path></svg>',
  check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"></path></svg>',
  export: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M7 8l5-5 5 5"></path><path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"></path></svg>',
  plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>',
  save: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h12l2 2v14H5z"></path><path d="M8 4v6h8V4M8 20v-6h8v6"></path></svg>',
  load: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5"></path><path d="M5 20h14"></path></svg>',
  edit: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 20 4.2-1 10.7-10.7a2.1 2.1 0 0 0-3-3L5.2 16 4 20Z"></path><path d="m14.5 6.5 3 3"></path></svg>',
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7V5Z"></path></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5v14M15 5v14"></path></svg>',
  chevron: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6"></path></svg>',
  trash: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13"></path></svg>'
});
const state = normalizeProject({ images: [], trialImages: [], script: [], trialScript: [], scriptText: '', trialScriptText: '', settings: { ...DEFAULT_SETTINGS } });
let activeTab = 'main';
let playing = false;
let playbackStart = 0;
let playbackTime = 0;
let playbackRaf = 0;
let previewOverride = null;
let installPrompt = null;
let saveTimer = 0;
let toastTimer = 0;
let profiles = [];
let settingsPreviewTab = 'cover';
let settingsPreviewRaf = 0;
let installedCustomFonts = [];
let profileSyncInFlight = false;
let settingsDirty = false;
let exportController = null;
let slideEditTarget = null;

function toast(message) {
  const element = $('#toast');
  element.textContent = message;
  element.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => element.classList.remove('show'), 2600);
}

function setSaveState(message, pending = false) {
  $('#saveState').textContent = message;
  $('#statusDot').style.background = pending ? '#ffca52' : '#c7ff36';
}

function scheduleSave() {
  setSaveState('Saving locally…', true);
  clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      await saveProject(state);
      setSaveState('Saved locally');
    } catch (error) {
      setSaveState('Could not save', true);
      console.error(error);
    }
  }, 350);
}

async function saveProjectNow() {
  clearTimeout(saveTimer);
  setSaveState('Saving locally…', true);
  await saveProject(state);
  setSaveState('Saved locally');
}

function setSettingsDirty(dirty = true) {
  settingsDirty = dirty;
  for (const button of [$('#saveProfileBtn'), $('#saveSettingsBtn')]) {
    if (!button) continue;
    const label = button === $('#saveSettingsBtn') ? 'Save changes' : 'Save profile';
    button.classList.toggle('save-dirty', dirty);
    button.setAttribute('aria-label', dirty ? `${label}, unsaved changes` : label);
    button.title = dirty ? 'Unsaved changes' : 'All changes saved';
  }
  const indicator = $('#profileSyncState');
  if (dirty && state.activeProfileId && indicator) {
    indicator.dataset.state = 'dirty';
    indicator.textContent = 'Profile has unpublished changes';
  } else if (!dirty && state.activeProfileId && indicator) {
    const active = profiles.find(profile => profile.id === state.activeProfileId);
    indicator.dataset.state = active?.syncState === 'pending' ? 'offline' : 'synced';
    indicator.textContent = active?.syncState === 'pending' ? 'Saved locally · publishing pending' : 'Profile shared with team';
  }
}

function showSaved(button, defaultLabel) {
  button.disabled = false;
  button.innerHTML = `${ICONS.check}<span>Saved</span>`;
  window.setTimeout(() => { button.innerHTML = `${ICONS.save}<span>${defaultLabel}</span>`; }, 1300);
}

function hydrateImage(item) {
  return normalizeMediaItem({ ...item, url: URL.createObjectURL(item.blob) }, state.settings);
}

function revokeImages(items) {
  items.forEach((item) => item.url && URL.revokeObjectURL(item.url));
}

function readVideoMetadata(url, name) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const timeout = setTimeout(() => reject(new Error(`Could not inspect ${name}.`)), 12000);
    video.preload = 'metadata'; video.muted = true; video.playsInline = true;
    video.addEventListener('loadedmetadata', () => { clearTimeout(timeout); resolve(Number.isFinite(video.duration) ? video.duration : 0); }, { once:true });
    video.addEventListener('error', () => { clearTimeout(timeout); reject(new Error(`${name} is not a supported MP4 or WebM video.`)); }, { once:true });
    video.src = url;
  });
}

async function filesToItems(files) {
  const supported = ['image/png', 'image/jpeg', 'image/webp', 'video/mp4', 'video/webm'];
  const items = [];
  for (const file of [...files].filter(file => supported.includes(file.type))) {
    if (file.size > 250 * 1024 * 1024) throw new Error(`${file.name} is larger than the 250 MB local limit.`);
    const url = URL.createObjectURL(file);
    let duration = 0;
    try { if (file.type.startsWith('video/')) duration = await readVideoMetadata(url, file.name); }
    catch (error) { URL.revokeObjectURL(url); throw error; }
    items.push(normalizeMediaItem({ id:uid(), name:file.name, type:file.type, blob:file, url, duration }, state.settings));
  }
  return items;
}

function actualSlideCount() {
  return projectSlideCount(state);
}

function draftStatus(input, fallback) {
  const value = input?.value ?? '';
  if (!value.trim()) return { count: 0, valid: true };
  try { return { count: parseScript(value).length, valid: true }; }
  catch { return { count: fallback, valid: false }; }
}

function renderMetrics() {
  const mainDraft = draftStatus($('#scriptInput'), state.script.length);
  const mediaOnly = mainDraft.valid && mainDraft.count === 0 && !$('#scriptInput').value.trim();
  const count = mediaOnly ? state.images.length : Math.min(state.images.length, mainDraft.count);
  const matched = mainDraft.valid && count > 0 && (mediaOnly || state.images.length === mainDraft.count);
  $('#metrics').innerHTML = [
    mediaOnly ? '<span class="metric">Mode: <strong>Media only</strong></span>' : `<span class="metric ${matched ? '' : 'bad'}">In script: <strong>${mainDraft.valid ? mainDraft.count : '—'}</strong></span>`,
    `<span class="metric ${matched ? '' : 'bad'}">Media: <strong>${state.images.length}</strong></span>`,
    `<span class="metric">Duration: <strong>${formatDuration(count * state.settings.duration)}</strong></span>`,
    `<span class="metric">${mediaOnly ? `${count} media slides` : state.images.length === 1 && mainDraft.count === 1 ? 'Cover-only' : `${count} slides`}</span>`
  ].join('');
  const trialDraft = draftStatus($('#trialScriptInput'), state.trialScript.length);
  const trialCount = Math.min(state.trialImages.length, trialDraft.count);
  const trialMatched = trialDraft.valid && state.trialImages.length === trialDraft.count && trialCount > 0;
  $('#trialMetrics').innerHTML = [
    `<span class="metric ${trialMatched ? '' : 'bad'}">Titles: <strong>${trialDraft.valid ? trialDraft.count : '—'}</strong></span>`,
    `<span class="metric ${trialMatched ? '' : 'bad'}">Covers: <strong>${state.trialImages.length}</strong></span>`,
    `<span class="metric">Variations: <strong>${trialCount}</strong></span>`
  ].join('');
}

function attachTouchReorder(element, index, cardSelector, move) {
  let targetIndex = index;
  let pointerId = null;
  let active = false;
  let holdTimer = 0;
  let startX = 0;
  let startY = 0;
  const cancelHold = () => { clearTimeout(holdTimer); holdTimer = 0; };
  const finish = (event) => {
    if (pointerId !== event.pointerId) return;
    cancelHold();
    element.classList.remove('dragging');
    document.querySelectorAll(`${cardSelector}.drag-target`).forEach(item => item.classList.remove('drag-target'));
    pointerId = null;
    const shouldMove = active && targetIndex !== index;
    active = false;
    if (shouldMove) move(index, targetIndex);
  };
  element.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('button')) return;
    pointerId = event.pointerId;
    targetIndex = index;
    startX = event.clientX;
    startY = event.clientY;
    holdTimer = window.setTimeout(() => {
      if (pointerId !== event.pointerId) return;
      active = true;
      element.setPointerCapture(pointerId);
      element.classList.add('dragging');
      navigator.vibrate?.(20);
    }, 260);
  });
  element.addEventListener('pointermove', (event) => {
    if (pointerId !== event.pointerId) return;
    if (!active) {
      if (Math.hypot(event.clientX - startX, event.clientY - startY) > 8) { cancelHold(); pointerId = null; }
      return;
    }
    event.preventDefault();
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest(cardSelector);
    const nextIndex = Number(target?.dataset.index);
    if (!target || !Number.isInteger(nextIndex)) return;
    document.querySelectorAll(`${cardSelector}.drag-target`).forEach(item => item.classList.toggle('drag-target', item === target));
    targetIndex = nextIndex;
  });
  element.addEventListener('pointerup', finish);
  element.addEventListener('pointercancel', finish);
}

function renderImageStrip(items, container, isTrial = false) {
  container.innerHTML = '';
  items.forEach((item, index) => {
    const chip = document.createElement('div');
    chip.className = 'image-chip';
    chip.draggable = !isTrial;
    chip.dataset.index = String(index);
    const media = isVideoItem(item)
      ? `<video src="${item.url}" aria-label="${escapeHtml(item.name)}" muted playsinline preload="metadata"></video><b class="media-badge">VIDEO</b>`
      : `<img src="${item.url}" alt="${escapeHtml(item.name)}">`;
    chip.innerHTML = `${media}<em>${index + 1}</em><button aria-label="Remove media">×</button>`;
    chip.querySelector('button').addEventListener('click', (event) => {
      event.preventDefault();
      URL.revokeObjectURL(item.url);
      items.splice(index, 1);
      syncProject();
    });
    if (!isTrial) {
      chip.addEventListener('dragstart', (event) => event.dataTransfer.setData('text/plain', String(index)));
      chip.addEventListener('dragover', (event) => event.preventDefault());
      chip.addEventListener('drop', (event) => {
        event.preventDefault();
        const raw = event.dataTransfer.getData('text/plain');
        if (raw === '') return;
        const from = Number(raw);
        if (!Number.isInteger(from) || from === index) return;
        const [moved] = state.images.splice(from, 1);
        state.images.splice(index, 0, moved);
        syncProject();
      });
      attachTouchReorder(chip, index, '.image-chip', moveMainImage);
    }
    container.append(chip);
  });
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character]);
}

function moveMainImage(from, to) {
  if (!moveItem(state.images, from, to)) return false;
  syncProject();
  return true;
}

function openSlideEditor(index, target = 'main') {
  const collection = target === 'trial' ? state.trialScript : state.script;
  if (!Number.isInteger(index) || index < 0 || index >= collection.length) return;
  const media = (target === 'trial' ? state.trialImages : state.images)[index];
  slideEditTarget = { index, target, mediaDraft:isVideoItem(media) ? { ...media } : null };
  $('#slideEditorTitle').textContent = target === 'trial' ? `Edit Trial ${index + 1}` : index === 0 ? 'Edit cover title' : `Edit Slide ${index + 1}`;
  $('#slideEditorText').value = collection[index] || '';
  $('#slideEditorError').textContent = '';
  const controls = $('#videoSlideControls');
  controls.hidden = !slideEditTarget.mediaDraft;
  $('#slideEditorDialog').classList.toggle('has-video', Boolean(slideEditTarget.mediaDraft));
  if (slideEditTarget.mediaDraft) {
    const item = slideEditTarget.mediaDraft;
    $('#videoFitMode').value = item.fitMode;
    $('#videoEndBehavior').value = item.endBehavior;
    $('#videoScale').value = Math.round(item.scale * 100);
    $('#videoPositionX').value = item.positionX;
    $('#videoPositionY').value = item.positionY;
    $('#videoTrimStart').value = item.trimStart;
    $('#videoTrimEnd').value = item.trimEnd || (item.duration ? Number(item.duration).toFixed(1) : '');
    $('#videoTrimStart').max = item.duration || 86400;
    $('#videoTrimEnd').max = item.duration || 86400;
    $('#videoPlaybackRate').value = String(item.playbackRate);
    updateVideoEditorOutputs();
  }
  $('#slideEditorDialog').showModal();
  if (slideEditTarget.mediaDraft) requestAnimationFrame(renderSlideEditorPreview);
  requestAnimationFrame(() => $('#slideEditorText').focus());
}

function updateVideoEditorOutputs() {
  $('#videoScaleOutput').textContent = `${$('#videoScale').value}%`;
  $('#videoXOutput').textContent = `${$('#videoPositionX').value}%`;
  $('#videoYOutput').textContent = `${$('#videoPositionY').value}%`;
}

async function renderSlideEditorPreview() {
  if (!slideEditTarget?.mediaDraft || !$('#slideEditorDialog').open) return;
  const item = slideEditTarget.mediaDraft;
  const title = $('#slideEditorText').value || 'Video background preview';
  const sample = { ...state, images:[item], script:[title] };
  try { await renderFrame($('#slideEditorCanvas').getContext('2d', { alpha:false }), sample, Math.min(1, state.settings.duration / 2)); }
  catch (error) { $('#slideEditorError').textContent = error.message; }
}

function readVideoEditorDraft() {
  if (!slideEditTarget?.mediaDraft) return;
  Object.assign(slideEditTarget.mediaDraft, normalizeMediaItem({
    ...slideEditTarget.mediaDraft,
    fitMode:$('#videoFitMode').value,
    endBehavior:$('#videoEndBehavior').value,
    scale:Number($('#videoScale').value) / 100,
    positionX:Number($('#videoPositionX').value),
    positionY:Number($('#videoPositionY').value),
    trimStart:Number($('#videoTrimStart').value),
    trimEnd:Number($('#videoTrimEnd').value),
    playbackRate:Number($('#videoPlaybackRate').value)
  }, state.settings));
  updateVideoEditorOutputs();
  renderSlideEditorPreview();
}

function saveSlideEdit() {
  if (!slideEditTarget) return;
  const value = $('#slideEditorText').value.trim();
  if (!value) {
    $('#slideEditorError').textContent = 'Slide text cannot be empty.';
    return;
  }
  const isTrial = slideEditTarget.target === 'trial';
  const collection = isTrial ? state.trialScript : state.script;
  collection[slideEditTarget.index] = value;
  if (slideEditTarget.mediaDraft) {
    const media = (isTrial ? state.trialImages : state.images)[slideEditTarget.index];
    Object.assign(media, slideEditTarget.mediaDraft);
  }
  const textKey = isTrial ? 'trialScriptText' : 'scriptText';
  const input = isTrial ? $('#trialScriptInput') : $('#scriptInput');
  state[textKey] = JSON.stringify(collection, null, 2);
  input.value = state[textKey];
  $('#slideEditorDialog').close();
  slideEditTarget = null;
  syncProject();
  toast('Slide content updated');
}

async function renderSlideGrid() {
  const grid = $('#slideGrid');
  grid.innerHTML = '';
  const count = actualSlideCount();
  const renderJobs = [];
  $('#emptyPreview').hidden = count > 0;
  for (let index = 0; index < count; index += 1) {
    const card = document.createElement('article');
    card.className = 'slide-card';
    card.draggable = true;
    card.dataset.index = String(index);
    const canvas = document.createElement('canvas');
    canvas.width = 270;
    canvas.height = 480;
    card.append(canvas);
    const meta = document.createElement('div');
    meta.className = 'slide-meta';
    const editButton = state.script.length ? `<button class="edit-mini" type="button" aria-label="Edit ${index === 0 ? 'cover title' : `slide ${index + 1}`}" title="Edit text">${ICONS.edit}</button>` : '';
    meta.innerHTML = `<span>${state.mode === 'media_only' ? `Media ${index + 1}` : index === 0 ? 'Cover' : `Slide ${index + 1}`}</span><div class="slide-actions">${editButton}<button class="play-mini" type="button" aria-label="Play from this slide">▶</button></div>`;
    card.append(meta);
    meta.querySelector('.edit-mini')?.addEventListener('click', () => openSlideEditor(index));
    meta.querySelector('.play-mini').addEventListener('click', () => openPlayback(index * state.settings.duration));
    card.addEventListener('dragstart', (event) => {
      if (event.target.closest('button')) return event.preventDefault();
      card.classList.add('dragging');
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('application/x-reelpro-slide', String(index));
    });
    card.addEventListener('dragend', () => card.classList.remove('dragging'));
    card.addEventListener('dragover', (event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; card.classList.add('drag-target'); });
    card.addEventListener('dragleave', () => card.classList.remove('drag-target'));
    card.addEventListener('drop', (event) => {
      event.preventDefault();
      card.classList.remove('drag-target');
      const raw = event.dataTransfer.getData('application/x-reelpro-slide');
      if (raw !== '') moveMainImage(Number(raw), index);
    });
    attachTouchReorder(card, index, '.slide-card[draggable="true"]', moveMainImage);
    grid.append(card);
    renderJobs.push(renderThumbnail(canvas, state, index).catch((error) => console.error(`Could not render slide ${index + 1}`, error)));
  }
  await Promise.all(renderJobs);
}

async function renderTrialGrid() {
  const grid = $('#trialGrid');
  grid.innerHTML = '';
  const count = Math.min(state.trialImages.length, state.trialScript.length);
  const renderJobs = [];
  $('#emptyTrials').hidden = count > 0;
  for (let index = 0; index < count; index += 1) {
    const card = document.createElement('article');
    card.className = 'slide-card';
    const canvas = document.createElement('canvas');
    canvas.width = 270;
    canvas.height = 480;
    card.append(canvas);
    const meta = document.createElement('div');
    meta.className = 'slide-meta';
    meta.innerHTML = `<span>Trial ${index + 1}</span><div class="slide-actions"><button class="edit-mini" type="button" aria-label="Edit trial ${index + 1}" title="Edit title">${ICONS.edit}</button><button class="play-mini" type="button" aria-label="Preview trial reel">▶</button></div>`;
    card.append(meta);
    meta.querySelector('.edit-mini').addEventListener('click', () => openSlideEditor(index, 'trial'));
    meta.querySelector('.play-mini').addEventListener('click', () => openPlayback(0, { image: state.trialImages[index], title: state.trialScript[index], label: `Trial Reel v${index + 1}` }));
    grid.append(card);
    renderJobs.push(renderThumbnail(canvas, state, 0, state.trialImages[index], state.trialScript[index]).catch((error) => console.error(`Could not render trial ${index + 1}`, error)));
  }
  await Promise.all(renderJobs);
}

async function refreshVisuals() {
  renderImageStrip(state.images, $('#imageStrip'));
  renderImageStrip(state.trialImages, $('#trialImageStrip'), true);
  renderMetrics();
  await Promise.all([renderSlideGrid(), renderTrialGrid()]);
  if ($('#settingsDialog').open) renderSettingsPreview();
}

function syncProject() {
  const normalized = normalizeProject(state);
  state.mode = normalized.mode;
  scheduleSave();
  refreshVisuals().catch(console.error);
}

function setScript(input, errorElement, key, textKey) {
  try {
    const parsed = parseScript(input.value);
    state[key] = parsed;
    state[textKey] = input.value;
    errorElement.textContent = '';
    syncProject();
    toast(parsed.length ? `${parsed.length} ${parsed.length === 1 ? 'title' : 'slides'} applied` : key === 'script' ? 'Media-only mode enabled' : 'Trial titles cleared');
  } catch (error) {
    errorElement.textContent = error.message;
  }
}

function setupDropzone(dropzone, input, target) {
  const add = async (files) => {
    try {
      const items = await filesToItems(files);
      if (!items.length) return toast('Choose JPG, PNG, WebP, MP4, or WebM media.');
      state[target].push(...items);
      syncProject();
      if (items.some(isVideoItem)) toast('Video added locally · edit the slide to adjust framing');
    } catch (error) { toast(error.message || 'Could not add that media.'); }
  };
  input.addEventListener('change', async () => { await add(input.files); input.value = ''; });
  ['dragenter', 'dragover'].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.add('dragover'); }));
  ['dragleave', 'drop'].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.remove('dragover'); }));
  dropzone.addEventListener('drop', (event) => { add(event.dataTransfer.files); });
}

function switchTab(tab) {
  activeTab = tab;
  $$('.tab-view').forEach((element) => element.classList.toggle('active', element.id === `${tab}Tab`));
  $$('.nav-item[data-tab]').forEach((element) => element.classList.toggle('active', element.dataset.tab === tab));
  $('#exportBtn').innerHTML = `${ICONS.export}<span>${tab === 'trials' ? 'Export trials' : 'Export reel'}</span>`;
}

function playbackDuration() {
  if (previewOverride) return Math.max(1, actualSlideCount()) * state.settings.duration;
  return actualSlideCount() * state.settings.duration;
}

async function drawPlayback(time) {
  const canvas = $('#playbackCanvas');
  const ctx = canvas.getContext('2d', { alpha: false });
  await renderFrame(ctx, state, time, previewOverride?.image, previewOverride?.title);
  const total = playbackDuration();
  $('#scrubber').value = total ? Math.round(time / total * 1000) : 0;
  $('#timeLabel').textContent = `${formatDuration(time)} / ${formatDuration(total)}`;
}

async function animatePlayback(now) {
  if (!playing) return;
  const total = playbackDuration();
  if (!total) { pausePlayback(); return; }
  playbackTime = (now - playbackStart) / 1000;
  if (playbackTime >= total) { playbackTime = 0; playbackStart = now; }
  await drawPlayback(playbackTime);
  playbackRaf = requestAnimationFrame(animatePlayback);
}

function pausePlayback() {
  playing = false;
  cancelAnimationFrame(playbackRaf);
  $('#playPauseBtn').innerHTML = ICONS.play;
  $('#playPauseBtn').setAttribute('aria-label', 'Play preview');
}

function resumePlayback() {
  if (!actualSlideCount() && !previewOverride) return toast('Add media first. Script text is optional.');
  playing = true;
  playbackStart = performance.now() - playbackTime * 1000;
  $('#playPauseBtn').innerHTML = ICONS.pause;
  $('#playPauseBtn').setAttribute('aria-label', 'Pause preview');
  playbackRaf = requestAnimationFrame(animatePlayback);
}

function openPlayback(startAt = 0, override = null) {
  if (!actualSlideCount() && !override) return toast('Add media first. Script text is optional.');
  if (override && (!override.image || !override.title)) return toast('Add a matching Trial Reel cover and title first.');
  previewOverride = override;
  playbackTime = startAt;
  $('#drawerTitle').textContent = override?.label || 'Main Reel';
  $('#playbackDrawer').classList.add('open');
  $('#drawerToggle').setAttribute('aria-label', 'Close live playback');
  drawPlayback(playbackTime);
  pausePlayback();
  resumePlayback();
}

async function renderSettingsPreview(previewTime = null) {
  const canvas = $('#settingsCanvas');
  const ctx = canvas.getContext('2d', { alpha: false });
  const index = settingsPreviewTab === 'body' ? 1 : 0;
  const sample = actualSlideCount() > index ? state : normalizeProject({ ...state, images: [{}, {}], script: ['Your [[powerful]] hero title', 'A clear body slide preview that shows typography and text background settings.'] });
  const localTime = previewTime === null ? Math.min(1, sample.settings.duration / 2) : previewTime % sample.settings.duration;
  await renderFrame(ctx, sample, index * sample.settings.duration + localTime);
  $('#settingsPreviewLabel').textContent = settingsPreviewTab === 'body'
    ? 'Body preview · loops automatically'
    : settingsPreviewTab === 'motion'
      ? 'Motion preview · loops automatically'
      : 'Cover preview · loops automatically';
}

function refreshFontOptions() {
  const available = [...STANDARD_FONTS, ...customFonts()];
  const fontSelects = [
    [$('#heroFontSetting'), state.settings.heroFont, DEFAULT_SETTINGS.heroFont],
    [$('#bodyFontSetting'), state.settings.bodyFont, DEFAULT_SETTINGS.bodyFont],
    [$('#subtextFontSetting'), state.settings.subtextFont, DEFAULT_SETTINGS.subtextFont]
  ];
  for (const [select, current, fallback] of fontSelects) {
    const selected = normalizeFontId(select.value, current);
    select.innerHTML = '';
    const standardGroup = document.createElement('optgroup'); standardGroup.label = 'ReelPro fonts';
    const customGroup = document.createElement('optgroup'); customGroup.label = 'Client fonts stored on this device';
    for (const font of available) (font.source === 'custom' ? customGroup : standardGroup).append(new Option(font.label || font.name, font.id));
    select.append(standardGroup); if (customGroup.children.length) select.append(customGroup);
    const resolved = normalizeFontId(selected, fallback);
    if (![...select.options].some(option => option.value === resolved)) select.add(new Option(`Missing font · ${resolved}`, resolved), 0);
    select.value = resolved;
  }
}

function renderCustomFontManager() {
  const list = $('#customFontList');
  if (!list) return;
  const fonts = customFonts();
  list.innerHTML = fonts.length ? '' : '<span class="font-empty">No client fonts uploaded on this device.</span>';
  for (const font of fonts) {
    const row = document.createElement('div'); row.className = 'custom-font-row';
    row.innerHTML = `<span><strong style="font-family:'${escapeHtml(font.family)}'">${escapeHtml(font.label || font.name)}</strong><small>${escapeHtml(font.name || '')}</small></span><button type="button" class="button small danger">Remove</button>`;
    row.querySelector('button').addEventListener('click', async () => {
      if (!confirm(`Remove ${font.label || font.name} from this device?`)) return;
      await deleteCustomFont(font.id); unregisterCustomFont(font.id); installedCustomFonts = installedCustomFonts.filter(item => item.id !== font.id);
      if (state.settings.heroFont === font.id) state.settings.heroFont = DEFAULT_SETTINGS.heroFont;
      if (state.settings.bodyFont === font.id) state.settings.bodyFont = DEFAULT_SETTINGS.bodyFont;
      if (state.settings.subtextFont === font.id) state.settings.subtextFont = DEFAULT_SETTINGS.subtextFont;
      setSettingsDirty(); refreshFontOptions(); populateSettings(); renderCustomFontManager(); syncProject(); toast('Client font removed');
    });
    list.append(row);
  }
}

async function addCustomFonts(files) {
  const allowed = /\.(ttf|otf|woff2?)$/i;
  for (const file of [...files]) {
    if (!allowed.test(file.name)) { toast(`${file.name} is not a supported font file`); continue; }
    if (file.size > 20 * 1024 * 1024) { toast(`${file.name} is larger than 20 MB`); continue; }
    const id = `custom:${uid()}`, label = file.name.replace(/\.(ttf|otf|woff2?)$/i, ''), record = {id,name:file.name,label,family:`ReelPro Custom ${id.slice(7)}`,weight:'400',style:'normal',blob:file,createdAt:Date.now()};
    try { await registerCustomFont(record); await saveCustomFont(record); installedCustomFonts.push(record); }
    catch (error) { toast(`${file.name} could not be loaded`); console.error(error); }
  }
  refreshFontOptions(); renderCustomFontManager(); toast('Client fonts saved locally');
}

function bindSettings() {
  refreshFontOptions();
  if (!$('#fontPairSetting')) {
    const label = document.createElement('label');
    label.innerHTML = 'Font pair<select id="fontPairSetting"><option value="instrument_google">Editorial Clean</option><option value="playfair_montserrat">Classic Modern</option><option value="cormorant_poppins">Luxury Social</option><option value="dmserif_google">Strong Editorial</option><option value="bebas_montserrat">Bold Creator</option><option value="bodoni_poppins">Fashion Luxury</option><option value="custom">Custom fonts</option></select>';
    $('#heroPresetSetting').closest('label').before(label);
  }
  const fontPairs = { instrument_google:['InstrumentSerif-Regular.ttf','GoogleSans-Medium.ttf'], playfair_montserrat:['PlayfairDisplay-Variable.ttf','Montserrat-Variable.ttf'], cormorant_poppins:['CormorantGaramond-Variable.ttf','Poppins-SemiBold.ttf'], dmserif_google:['DMSerifDisplay-Regular.ttf','GoogleSans-Medium.ttf'], bebas_montserrat:['BebasNeue-Regular.ttf','Montserrat-Variable.ttf'], bodoni_poppins:['BodoniModa-Variable.ttf','Poppins-Regular.ttf'] };
  $('#fontPairSetting').addEventListener('change', event => { state.settings.fontPair = event.target.value; const pair = fontPairs[event.target.value]; if (pair) { state.settings.heroFont = pair[0]; state.settings.bodyFont = pair[1]; populateSettings(); renderSettingsPreview(); } setSettingsDirty(); });
  const fontManager = document.createElement('fieldset');
  fontManager.className = 'client-font-manager';
  fontManager.innerHTML = '<legend>Client fonts</legend><div class="client-font-toolbar"><div><strong>Local custom fonts</strong><small>TTF, OTF, WOFF or WOFF2 · stored only on this device</small></div><label class="button small ghost">＋ Upload fonts<input id="customFontInput" type="file" accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2" multiple hidden></label></div><div id="customFontList" class="custom-font-list"></div><p class="font-license-note">Only upload fonts you are licensed to use. Missing fonts are blocked before export instead of silently replaced.</p>';
  $('[data-settings-panel="motion"]').append(fontManager);
  $('#customFontInput').addEventListener('change', async event => { await addCustomFonts(event.target.files); event.target.value = ''; });
  renderCustomFontManager();
  const mapping = {
    heroPresetSetting:'heroPreset',heroFontSetting:'heroFont',heroAlignSetting:'heroAlign',heroSizeSetting:'heroSize',heroSupportSizeSetting:'heroSupportSize',heroPowerSizeSetting:'heroPowerSize',titleYSetting:'titleY',coverTextAnimationSetting:'coverTextAnimation',coverAnimationDurationSetting:'coverTextAnimationDuration',textColorSetting:'textColor',accentColorSetting:'accentColor',titleMaterialSetting:'titleMaterial',goldApplySetting:'goldApply',goldToneSetting:'goldTone',goldDepthSetting:'goldDepth',goldHighlightSetting:'goldHighlight',goldContrastSetting:'goldContrast',goldTextureSetting:'goldTexture',goldStrokeSetting:'goldStroke',coverBackgroundModeSetting:'coverBackgroundMode',coverBackgroundColorSetting:'coverBackgroundColor',coverBackgroundOpacitySetting:'coverBackgroundOpacity',coverBackgroundRadiusSetting:'coverBackgroundRadius',coverPaddingXSetting:'coverBackgroundPaddingX',coverPaddingYSetting:'coverBackgroundPaddingY',coverBorderEnabledSetting:'coverBorderEnabled',coverBorderColorSetting:'coverBorderColor',coverBorderOpacitySetting:'coverBorderOpacity',coverBorderWidthSetting:'coverBorderWidth',heroShadowSetting:'heroShadow',heroShadowColorSetting:'heroShadowColor',heroShadowOpacitySetting:'heroShadowOpacity',heroShadowBlurSetting:'heroShadowBlur',heroShadowXSetting:'heroShadowX',heroShadowYSetting:'heroShadowY',overlayEnabledSetting:'overlayEnabled',overlaySetting:'overlayOpacity',subtextEnabledSetting:'subtextEnabled',subtextSetting:'subtext',subtextFontSetting:'subtextFont',subtextAlignSetting:'subtextAlign',subtextSizeSetting:'subtextSize',subtextYSetting:'subtextY',subtextGapSetting:'subtextGap',subtextDelaySetting:'subtextDelay',subtextAnimationSetting:'subtextAnimation',subtextOutAnimationSetting:'subtextOutAnimation',subtextExitSetting:'subtextExitBeforeEnd',subtextBackgroundSetting:'subtextBackground',subtextBackgroundColorSetting:'subtextBackgroundColor',subtextShadowSetting:'subtextShadow',subtextShadowOpacitySetting:'subtextShadowOpacity',subtextShadowBlurSetting:'subtextShadowBlur',subtextShadowXSetting:'subtextShadowX',subtextShadowYSetting:'subtextShadowY',
    bodyFontSetting:'bodyFont',bodySizeSetting:'bodySize',bodyAlignSetting:'bodyAlign',bodyYSetting:'bodyY',bodyTextAnimationSetting:'bodyTextAnimation',bodyAnimationDurationSetting:'bodyTextAnimationDuration',bodyTextColorSetting:'bodyTextColor',bodyCardModeSetting:'bodyCardMode',cardColorSetting:'cardColor',cardOpacitySetting:'cardOpacity',bodyCardRadiusSetting:'bodyCardRadius',bodyPaddingXSetting:'bodyCardPaddingX',bodyPaddingYSetting:'bodyCardPaddingY',bodyBorderEnabledSetting:'bodyBorderEnabled',bodyBorderColorSetting:'bodyBorderColor',bodyBorderOpacitySetting:'bodyBorderOpacity',bodyBorderWidthSetting:'bodyBorderWidth',bodyShadowSetting:'bodyShadow',bodyShadowColorSetting:'bodyShadowColor',bodyShadowOpacitySetting:'bodyShadowOpacity',bodyShadowBlurSetting:'bodyShadowBlur',bodyShadowXSetting:'bodyShadowX',bodyShadowYSetting:'bodyShadowY',
    durationSetting:'duration',motionSetting:'motion',transitionModeSetting:'transitionMode',transitionDurationSetting:'transitionDuration',motionSmoothnessSetting:'motionSmoothness',videoFitSetting:'videoFitMode',videoXSetting:'videoPositionX',videoYSetting:'videoPositionY',videoSpeedSetting:'videoPlaybackRate',videoEndSetting:'videoEndBehavior',watermarkSetting:'watermark',socialIconSetting:'socialIcon',watermarkSizeSetting:'watermarkSize',watermarkOpacitySetting:'watermarkOpacity',dialogQualitySetting:'outputQuality'
  };
  for (const [id, key] of Object.entries(mapping)) {
    const element = $(`#${id}`);
    const eventName = ['text','number','range','color'].includes(element.type) ? 'input' : 'change';
    element.addEventListener(eventName, () => {
      state.settings[key] = element.type === 'checkbox' ? element.checked : element.type === 'range' || element.type === 'number' ? Number(element.value) : element.value;
      if (key === 'outputQuality') $('#qualitySetting').value = element.value;
      if (key === 'titleMaterial') updateMaterialControls();
      setSettingsDirty();
      updateSettingOutputs();
      renderSettingsPreview();
    });
  }
  $('#zoomSetting').addEventListener('input', () => {
    state.settings.zoom = 1 + Number($('#zoomSetting').value) / 100;
    setSettingsDirty();
    updateSettingOutputs();
    renderSettingsPreview();
  });
  $('#videoScaleSetting').addEventListener('input', () => {
    state.settings.videoScale = Number($('#videoScaleSetting').value) / 100;
    setSettingsDirty(); renderSettingsPreview();
  });
  $('#heroPresetSetting').addEventListener('change', () => {
    const presets = {
      classic:[82,72,92,45,'center'], editorial:[78,66,98,42,'center'], minimal:[80,80,80,50,'center'], power_stack:[72,56,104,50,'center'], dual_font:[72,58,96,50,'center'], magazine:[78,48,100,42,'left'], oversized_keyword:[70,50,130,50,'center'], bottom_editorial:[78,58,96,72,'left'], framed:[66,58,88,50,'center'], split_headline:[72,56,108,48,'left'], minimal_luxury:[68,44,82,44,'center'], bold_condensed:[96,70,118,50,'center']
    };
    const values = presets[state.settings.heroPreset]; if (!values) return;
    [state.settings.heroSize,state.settings.heroSupportSize,state.settings.heroPowerSize,state.settings.titleY,state.settings.heroAlign] = values;
    populateSettings(); renderSettingsPreview();
  });
}

function populateSettings() {
  const reverse = { heroPresetSetting:'heroPreset',heroFontSetting:'heroFont',heroAlignSetting:'heroAlign',heroSizeSetting:'heroSize',heroSupportSizeSetting:'heroSupportSize',heroPowerSizeSetting:'heroPowerSize',titleYSetting:'titleY',coverTextAnimationSetting:'coverTextAnimation',coverAnimationDurationSetting:'coverTextAnimationDuration',textColorSetting:'textColor',accentColorSetting:'accentColor',titleMaterialSetting:'titleMaterial',goldApplySetting:'goldApply',goldToneSetting:'goldTone',goldDepthSetting:'goldDepth',goldHighlightSetting:'goldHighlight',goldContrastSetting:'goldContrast',goldTextureSetting:'goldTexture',goldStrokeSetting:'goldStroke',coverBackgroundModeSetting:'coverBackgroundMode',coverBackgroundColorSetting:'coverBackgroundColor',coverBackgroundOpacitySetting:'coverBackgroundOpacity',coverBackgroundRadiusSetting:'coverBackgroundRadius',coverPaddingXSetting:'coverBackgroundPaddingX',coverPaddingYSetting:'coverBackgroundPaddingY',coverBorderColorSetting:'coverBorderColor',coverBorderOpacitySetting:'coverBorderOpacity',coverBorderWidthSetting:'coverBorderWidth',heroShadowColorSetting:'heroShadowColor',heroShadowOpacitySetting:'heroShadowOpacity',heroShadowBlurSetting:'heroShadowBlur',heroShadowXSetting:'heroShadowX',heroShadowYSetting:'heroShadowY',overlaySetting:'overlayOpacity',subtextSetting:'subtext',subtextFontSetting:'subtextFont',subtextAlignSetting:'subtextAlign',subtextSizeSetting:'subtextSize',subtextYSetting:'subtextY',subtextGapSetting:'subtextGap',subtextDelaySetting:'subtextDelay',subtextAnimationSetting:'subtextAnimation',subtextOutAnimationSetting:'subtextOutAnimation',subtextExitSetting:'subtextExitBeforeEnd',subtextBackgroundColorSetting:'subtextBackgroundColor',subtextShadowOpacitySetting:'subtextShadowOpacity',subtextShadowBlurSetting:'subtextShadowBlur',subtextShadowXSetting:'subtextShadowX',subtextShadowYSetting:'subtextShadowY',bodyFontSetting:'bodyFont',bodySizeSetting:'bodySize',bodyAlignSetting:'bodyAlign',bodyYSetting:'bodyY',bodyTextAnimationSetting:'bodyTextAnimation',bodyAnimationDurationSetting:'bodyTextAnimationDuration',bodyTextColorSetting:'bodyTextColor',bodyCardModeSetting:'bodyCardMode',cardColorSetting:'cardColor',cardOpacitySetting:'cardOpacity',bodyCardRadiusSetting:'bodyCardRadius',bodyPaddingXSetting:'bodyCardPaddingX',bodyPaddingYSetting:'bodyCardPaddingY',bodyBorderColorSetting:'bodyBorderColor',bodyBorderOpacitySetting:'bodyBorderOpacity',bodyBorderWidthSetting:'bodyBorderWidth',bodyShadowColorSetting:'bodyShadowColor',bodyShadowOpacitySetting:'bodyShadowOpacity',bodyShadowBlurSetting:'bodyShadowBlur',bodyShadowXSetting:'bodyShadowX',bodyShadowYSetting:'bodyShadowY',durationSetting:'duration',motionSetting:'motion',transitionModeSetting:'transitionMode',transitionDurationSetting:'transitionDuration',motionSmoothnessSetting:'motionSmoothness',videoFitSetting:'videoFitMode',videoXSetting:'videoPositionX',videoYSetting:'videoPositionY',videoSpeedSetting:'videoPlaybackRate',videoEndSetting:'videoEndBehavior',watermarkSetting:'watermark',socialIconSetting:'socialIcon',watermarkSizeSetting:'watermarkSize',watermarkOpacitySetting:'watermarkOpacity',dialogQualitySetting:'outputQuality' };
  const values = Object.fromEntries(Object.entries(reverse).map(([id,key]) => [id,state.settings[key]])); values.zoomSetting = Math.round((state.settings.zoom - 1) * 100); values.videoScaleSetting = Math.round(state.settings.videoScale * 100);
  Object.entries(values).forEach(([id, value]) => { $(`#${id}`).value = value; });
  ({heroShadowSetting:'heroShadow',overlayEnabledSetting:'overlayEnabled',subtextEnabledSetting:'subtextEnabled',subtextShadowSetting:'subtextShadow',bodyShadowSetting:'bodyShadow'});
  for (const [id,key] of Object.entries({coverBorderEnabledSetting:'coverBorderEnabled',heroShadowSetting:'heroShadow',overlayEnabledSetting:'overlayEnabled',subtextEnabledSetting:'subtextEnabled',subtextBackgroundSetting:'subtextBackground',subtextShadowSetting:'subtextShadow',bodyBorderEnabledSetting:'bodyBorderEnabled',bodyShadowSetting:'bodyShadow'})) $(`#${id}`).checked = Boolean(state.settings[key]);
  $('#fontPairSetting').value = state.settings.fontPair || 'custom';
  updateMaterialControls();
  updateSettingOutputs();
}

function updateMaterialControls() {
  const gold = state.settings.titleMaterial === 'gold_embossed';
  $('#goldMaterialControls').hidden = !gold;
  $('#goldMaterialControls').closest('fieldset').classList.toggle('material-active', gold);
}

function updateSettingOutputs() {
  $('#overlayOutput').textContent = `${state.settings.overlayOpacity}%`;
  $('#titleYOutput').textContent = `${state.settings.titleY}%`;
  $('#bodyYOutput').textContent = `${state.settings.bodyY}%`;
}

function refreshProfileSelects() {
  for (const select of [$('#profileSelect'), $('#sidebarProfileSelect'), $('#mobileProfileSelect')]) {
    select.innerHTML = '<option value="">Default settings</option>';
    for (const profile of profiles.sort((a,b) => a.name.localeCompare(b.name))) select.add(new Option(profile.name, profile.id));
    select.value = state.activeProfileId || '';
  }
  const active = profiles.find(profile => profile.id === state.activeProfileId);
  $('#profileName').value = active?.name || '';
}

async function refreshSharedProfiles({ applyActiveChanges = true } = {}) {
  if (profileSyncInFlight) return;
  profileSyncInFlight = true;
  const previousActive = profiles.find(profile => profile.id === state.activeProfileId);
  try {
    const next = (await listProfiles()).map(profile => ({ ...profile, settings: importProfileSettings(profile.settings) }));
    const nextActive = next.find(profile => profile.id === state.activeProfileId);
    profiles = next;
    if (applyActiveChanges && nextActive && Number(nextActive.version || 0) !== Number(previousActive?.version || 0)) {
      state.settings = importProfileSettings(nextActive.settings);
      populateSettings();
      setSettingsDirty(false);
      syncProject();
      toast(`${nextActive.name} updated from the shared library`);
    } else if (applyActiveChanges && state.activeProfileId && !nextActive) {
      state.activeProfileId = '';
      scheduleSave();
      toast('The active profile was removed from the shared library');
    }
    refreshProfileSelects();
  } finally {
    profileSyncInFlight = false;
  }
}

async function applyProfile(id) {
  const profile = profiles.find(item => item.id === id);
  state.activeProfileId = profile?.id || '';
  state.settings = profile ? importProfileSettings(profile.settings) : { ...DEFAULT_SETTINGS, ...(id ? {} : state.settings) };
  state.settings.heroFont = normalizeFontId(state.settings.heroFont, DEFAULT_SETTINGS.heroFont);
  state.settings.bodyFont = normalizeFontId(state.settings.bodyFont, DEFAULT_SETTINGS.bodyFont);
  state.settings.subtextFont = normalizeFontId(state.settings.subtextFont, DEFAULT_SETTINGS.subtextFont);
  refreshProfileSelects(); populateSettings(); setSettingsDirty(false); syncProject();
  try { await saveProjectNow(); } catch (error) { console.error(error); }
  toast(profile ? `${profile.name} loaded` : 'Using current default settings');
}

async function handleProfileSelection(event) {
  const previous = state.activeProfileId || '';
  if (settingsDirty && !confirm('Discard the unsaved profile changes and switch profiles?')) {
    event.target.value = previous;
    return;
  }
  await applyProfile(event.target.value);
}

function startSettingsPreviewLoop() {
  cancelAnimationFrame(settingsPreviewRaf);
  const started = performance.now();
  const tick = async (now) => {
    if (!$('#settingsDialog').open) return;
    await renderSettingsPreview((now - started) / 1000);
    settingsPreviewRaf = requestAnimationFrame(tick);
  };
  settingsPreviewRaf = requestAnimationFrame(tick);
}

async function exportProject() {
  const mainCount = actualSlideCount();
  const trialCount = Math.min(state.trialImages.length, state.trialScript.length);
  const scope = state.settings.exportScope || 'auto';
  const { exportTrialsOnly, includeMain, includeTrials } = resolveExportSelection({ mainCount, trialCount, scope, activeTab });
  const jobs = [];
  const quality = state.settings.outputQuality || 'hd';
  if (includeMain) jobs.push({ name: `reelpro-main-${quality}.mp4`, options: { quality } });
  for (let index = 0; includeTrials && index < trialCount; index += 1) {
    jobs.push({ name: `reelpro-trial-${index + 1}-${quality}.mp4`, options: { imageOverride: state.trialImages[index], titleOverride: state.trialScript[index], quality } });
  }
  if (exportTrialsOnly && !trialCount) return toast('Add matching trial covers and titles first.');
  if (!jobs.length) return toast(activeTab === 'trials' ? 'Add matching trial covers and titles first.' : 'Add media to the Main Reel first. Script text is optional.');
  const button = $('#exportBtn');
  button.innerHTML = '<span>Checking device…</span>';
  button.disabled = true;
  const support = await inspectExportSupport(quality);
  if (!support.supported) {
    toast(support.reason);
    button.disabled = false;
    switchTab(activeTab);
    return;
  }
  exportController = new AbortController();
  $('#cancelExportBtn').hidden = false;
  try {
    for (let index = 0; index < jobs.length; index += 1) {
      const job = jobs[index];
      button.textContent = `Rendering ${index + 1}/${jobs.length} · 0%`;
      const blob = await exportMp4(state, (progress) => { button.textContent = `Rendering ${index + 1}/${jobs.length} · ${Math.round(progress * 100)}%`; }, { ...job.options, support, signal:exportController.signal });
      downloadBlob(blob, job.name);
    }
    toast(`${jobs.length} ${jobs.length === 1 ? 'reel' : 'reels'} exported locally`);
  } catch (error) {
    if (error instanceof ExportCancelledError) toast('Export cancelled safely.');
    else { console.error(error); toast(error.message || 'Browser export failed.'); }
  } finally {
    exportController = null;
    $('#cancelExportBtn').hidden = true;
    button.disabled = false;
    switchTab(activeTab);
  }
}

async function copyText(text, message) {
  await navigator.clipboard.writeText(text);
  toast(message);
}

function exportProfilesFile() {
  const active = profiles.find(profile => profile.id === state.activeProfileId);
  const payload = {
    format: PROFILE_TRANSFER_FORMAT,
    version: PROFILE_TRANSFER_VERSION,
    exported_at: new Date().toISOString(),
    source: 'pwa',
    active_profile_name: active?.name || null,
    profiles: profiles.map(profile => ({ name: profile.name, settings: exportProfileSettings(profile.settings) }))
  };
  downloadBlob(new Blob([JSON.stringify(payload, null, 2)], {type:'application/json'}), `reelpro-profiles-${new Date().toISOString().slice(0,10)}.json`);
  toast(`${profiles.length} client profile${profiles.length === 1 ? '' : 's'} exported`);
}

async function importProfilesFile(file) {
  if (!file) return;
  try {
    const payload = JSON.parse(await file.text());
    if (payload?.format !== PROFILE_TRANSFER_FORMAT || payload.version !== PROFILE_TRANSFER_VERSION || !Array.isArray(payload.profiles)) throw new Error('Choose a valid ReelPro profiles JSON file.');
    if (payload.profiles.length > 250) throw new Error('A profile file can contain no more than 250 profiles.');
    const byName = new Map(profiles.map(profile => [profile.name.toLocaleLowerCase(), profile]));
    let created = 0, updated = 0, pending = 0;
    for (const item of payload.profiles) {
      const name = String(item?.name || '').trim();
      if (!name || name.length > 80) throw new Error('Every imported profile needs a name of 1–80 characters.');
      const existing = byName.get(name.toLocaleLowerCase());
      const stored = await saveProfile({ id: existing?.id || uid(), name, settings: exportProfileSettings(importProfileSettings(item.settings)), version: Number(existing?.version || 0) });
      const saved = { ...stored, settings: importProfileSettings(stored.settings) };
      if (saved.syncState === 'pending') pending += 1;
      byName.set(name.toLocaleLowerCase(), saved);
      if (existing) updated += 1; else created += 1;
    }
    profiles = [...byName.values()];
    const active = profiles.find(profile => profile.name.toLocaleLowerCase() === String(payload.active_profile_name || '').toLocaleLowerCase());
    if (active) { state.activeProfileId = active.id; state.settings = { ...DEFAULT_SETTINGS, ...active.settings }; state.settings.heroFont = normalizeFontId(state.settings.heroFont, DEFAULT_SETTINGS.heroFont); state.settings.bodyFont = normalizeFontId(state.settings.bodyFont, DEFAULT_SETTINGS.bodyFont); state.settings.subtextFont = normalizeFontId(state.settings.subtextFont, DEFAULT_SETTINGS.subtextFont); setSettingsDirty(false); }
    refreshProfileSelects(); populateSettings(); syncProject();
    if (pending) toast(`Profiles saved locally · ${pending} waiting to sync`);
    else toast(`Profiles shared with team · ${created} added, ${updated} updated`);
  } catch (error) {
    toast(error instanceof SyntaxError ? 'Choose a valid ReelPro profiles JSON file.' : error.message);
  }
}

async function persistCurrentProfile(button = $('#saveProfileBtn')) {
  const name = $('#profileName').value.trim();
  if (!name) { toast('Enter a profile name first.'); return false; }
  const defaultLabel = button === $('#saveSettingsBtn') ? 'Save changes' : 'Save profile';
  const id = state.activeProfileId || uid();
  const current = profiles.find(item => item.id === id);
  button.disabled = true;
  button.innerHTML = `${ICONS.save}<span>Saving…</span>`;
  try {
    const stored = await saveProfile({ id, name, settings: exportProfileSettings(state.settings), version: Number(current?.version || 0) });
    const saved = { ...stored, settings: importProfileSettings(stored.settings) };
    profiles = profiles.filter(item => item.id !== id);
    profiles.push(saved);
    state.activeProfileId = id;
    refreshProfileSelects();
    await saveProjectNow();
    setSettingsDirty(false);
    showSaved(button, defaultLabel);
    toast(saved.syncState === 'pending' ? `${name} saved offline · sync pending` : `${name} shared with the team`);
    return true;
  } catch (error) {
    button.disabled = false;
    button.innerHTML = `${ICONS.save}<span>${defaultLabel}</span>`;
    if (error.status === 409 && error.current?.id === id && confirm('This profile changed on another device. Overwrite the shared version with your current settings?')) {
      try {
        const stored = await saveProfile({ id, name, settings: exportProfileSettings(state.settings), version: Number(error.current.version) });
        const saved = { ...stored, settings: importProfileSettings(stored.settings) };
        profiles = profiles.filter(item => item.id !== id);
        profiles.push(saved);
        state.activeProfileId = id;
        refreshProfileSelects();
        await saveProjectNow();
        setSettingsDirty(false);
        showSaved(button, defaultLabel);
        toast(`${name} updated with your current settings`);
        return true;
      } catch (retryError) {
        toast(retryError.message || 'The profile changed again. Sync and retry.');
      }
    } else if (error.status === 409) {
      if (error.current) {
        profiles = profiles.filter(item => item.id !== error.current.id);
        profiles.push({ ...error.current, settings: importProfileSettings(error.current.settings) });
        refreshProfileSelects();
      }
      toast(error.current?.id !== id ? 'That profile name is already used.' : 'Your edit was not shared; the newer team version is preserved.');
    } else toast(error.message || 'Could not save the profile.');
    return false;
  }
}

function bindEvents() {
  setupDropzone($('#dropzone'), $('#imageInput'), 'images');
  setupDropzone($('#trialDropzone'), $('#trialImageInput'), 'trialImages');
  $('#applyScriptBtn').addEventListener('click', () => setScript($('#scriptInput'), $('#scriptError'), 'script', 'scriptText'));
  $('#applyTrialsBtn').addEventListener('click', () => setScript($('#trialScriptInput'), $('#trialScriptError'), 'trialScript', 'trialScriptText'));
  $('#saveSlideEditBtn').addEventListener('click', saveSlideEdit);
  $('#slideEditorDialog').addEventListener('close', () => { slideEditTarget = null; $('#slideEditorError').textContent = ''; });
  for (const id of ['videoFitMode','videoEndBehavior','videoScale','videoPositionX','videoPositionY','videoTrimStart','videoTrimEnd','videoPlaybackRate']) {
    $(`#${id}`).addEventListener(['range','number'].includes($(`#${id}`).type) ? 'input' : 'change', readVideoEditorDraft);
  }
  $('#resetVideoFraming').addEventListener('click', () => {
    if (!slideEditTarget?.mediaDraft) return;
    Object.assign(slideEditTarget.mediaDraft, { fitMode:state.settings.videoFitMode, scale:state.settings.videoScale, positionX:state.settings.videoPositionX, positionY:state.settings.videoPositionY, playbackRate:state.settings.videoPlaybackRate, endBehavior:state.settings.videoEndBehavior, trimStart:0, trimEnd:0 });
    $('#videoFitMode').value = slideEditTarget.mediaDraft.fitMode; $('#videoEndBehavior').value = slideEditTarget.mediaDraft.endBehavior; $('#videoScale').value = Math.round(slideEditTarget.mediaDraft.scale * 100); $('#videoPositionX').value = slideEditTarget.mediaDraft.positionX; $('#videoPositionY').value = slideEditTarget.mediaDraft.positionY; $('#videoTrimStart').value = 0; $('#videoTrimEnd').value = ''; $('#videoPlaybackRate').value = String(slideEditTarget.mediaDraft.playbackRate); updateVideoEditorOutputs(); renderSlideEditorPreview();
  });
  $('#slideEditorText').addEventListener('input', () => { if (slideEditTarget?.mediaDraft) renderSlideEditorPreview(); });
  $('#scriptInput').addEventListener('input', () => { state.scriptText = $('#scriptInput').value; renderMetrics(); scheduleSave(); });
  $('#trialScriptInput').addEventListener('input', () => { state.trialScriptText = $('#trialScriptInput').value; renderMetrics(); scheduleSave(); });
  $('#copySingleFormatBtn').addEventListener('click', () => copyText(`Create a SINGLE-IMAGE ReelPro script for one cover image.

Return only the final valid JSON array. Do not add a markdown fence, explanation, slide number, or commentary.

SYMBOL RULES:
- [[power phrase]] marks the main cover phrase that receives the cover emphasis style. Use exactly one balanced [[...]] pair.
- **bold phrase** makes text bold. You may use more than one balanced **...** pair when useful, but keep emphasis selective.
- \\n creates an intentional line break inside the title.
- For quotations inside text, use curly quotes “like this” or escape straight quotes as \\"like this\\".

REQUIRED SINGLE-IMAGE FORMAT:
[
  "A concise title with one [[power phrase]] and optional **bold emphasis**"
]`, 'Single format copied'));
  $('#copyFormatBtn').addEventListener('click', () => copyText(`Convert the supplied content into a MULTIPLE-SLIDE ReelPro script.

Return only the final valid JSON array. Do not add a markdown fence, explanation, slide numbers, or commentary. Preserve all important information and create as many slide strings as needed without truncating the ending. The number of strings must match the number of images.

SYMBOL RULES:
- The first string is the cover title. Use exactly one balanced [[power phrase]] pair there for the main cover emphasis. Do not use [[...]] on body slides.
- **bold phrase** makes text bold. Multiple balanced **...** pairs are supported on the same slide; use them selectively around important words or short phrases.
- \\n creates a line break inside one slide.
- \\n\\n creates a blank line. Use it on the final slide to separate the main message from the CTA.
- For quotations inside a slide, use curly quotes “like this” or escape straight quotes as \\"like this\\".

REQUIRED MULTIPLE-SLIDE FORMAT:
[
  "Cover title with one [[power phrase]]",
  "Body slide 1 with **important words**",
  "Body slide 2 with **first emphasis** and **second emphasis**",
  "Add one quoted string for every additional slide",
  "Final insight.\\n\\n**Follow for more.**"
]`, 'Multiple format copied'));
  $('#trialCopyFormatBtn').addEventListener('click', () => copyText('["Alternative hero title 1", "Alternative hero title 2"]', 'Trial reel format copied'));
  $('#trialCopyPromptBtn').addEventListener('click', () => copyText('Create alternative cover hooks for the same reel. Return only a valid JSON array of short hero-title strings, one title per trial cover image. Do not rewrite body slides. Mark only the strongest power words as [[power words]]. Do not add markdown or commentary.', 'Trial reel AI prompt copied'));
  $('#copySingleFormatBtn').innerHTML = `${ICONS.copy}<span>Single Format</span>`;
  $('#copyFormatBtn').innerHTML = `${ICONS.copy}<span>Multiple Format</span>`;
  $('#applyScriptBtn').innerHTML = `${ICONS.check}<span>Apply script</span>`;
  $('#trialCopyFormatBtn').innerHTML = `${ICONS.copy}<span>Copy Format</span>`;
  $('#trialCopyPromptBtn').innerHTML = `${ICONS.sparkle}<span>Copy AI Prompt</span>`;
  $('#applyTrialsBtn').innerHTML = `${ICONS.check}<span>Apply titles</span>`;
  $('#drawerToggle').innerHTML = ICONS.chevron;
  $('#playPauseBtn').innerHTML = ICONS.play;
  $('#newProfileBtn').innerHTML = `${ICONS.plus}<span>New</span>`;
  $('#saveProfileBtn').innerHTML = `${ICONS.save}<span>Save profile</span>`;
  $('#deleteProfileBtn').innerHTML = `${ICONS.trash}<span>Delete</span>`;
  const profileActions = $('#saveProfileBtn').parentElement;
  const exportProfilesButton = document.createElement('button');
  exportProfilesButton.type = 'button'; exportProfilesButton.id = 'exportProfilesBtn'; exportProfilesButton.className = 'button small ghost'; exportProfilesButton.innerHTML = `${ICONS.export}<span>Export</span>`;
  const importProfilesButton = document.createElement('button');
  importProfilesButton.type = 'button'; importProfilesButton.id = 'importProfilesBtn'; importProfilesButton.className = 'button small ghost'; importProfilesButton.innerHTML = `${ICONS.load}<span>Import</span>`;
  const importProfilesInput = document.createElement('input');
  importProfilesInput.type = 'file'; importProfilesInput.id = 'importProfilesInput'; importProfilesInput.accept = 'application/json,.json'; importProfilesInput.hidden = true;
  profileActions.insertBefore(exportProfilesButton, $('#deleteProfileBtn'));
  profileActions.insertBefore(importProfilesButton, $('#deleteProfileBtn'));
  profileActions.append(importProfilesInput);
  exportProfilesButton.addEventListener('click', exportProfilesFile);
  importProfilesButton.addEventListener('click', () => importProfilesInput.click());
  importProfilesInput.addEventListener('change', async () => { await importProfilesFile(importProfilesInput.files[0]); importProfilesInput.value = ''; });
  $('#saveSettingsBtn').innerHTML = `${ICONS.save}<span>Save changes</span>`;
  $('#exportScopeSetting').addEventListener('change', () => { state.settings.exportScope = $('#exportScopeSetting').value; setSettingsDirty(); scheduleSave(); });
  $('#sidebarCollapseBtn').addEventListener('click', () => {
    document.body.classList.toggle('sidebar-collapsed');
    const collapsed = document.body.classList.contains('sidebar-collapsed');
    $('#sidebarCollapseBtn span').textContent = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
    $('#sidebarCollapseBtn').setAttribute('aria-label', collapsed ? 'Expand sidebar' : 'Collapse sidebar');
  });
  $('#clearImagesBtn').addEventListener('click', () => { revokeImages(state.images); state.images = []; syncProject(); });
  $('#newProjectBtn').addEventListener('click', async () => {
    if (!confirm('Create a new reel? The current local project will be cleared.')) return;
    revokeImages([...state.images, ...state.trialImages]);
    Object.assign(state, normalizeProject({ images: [], trialImages: [], script: [], trialScript: [], scriptText: '', trialScriptText: '', settings: { ...DEFAULT_SETTINGS } }));
    await clearProject();
    $('#scriptInput').value = '';
    $('#trialScriptInput').value = '';
    syncProject();
  });
  $$('.nav-item[data-tab]').forEach((button) => button.addEventListener('click', () => switchTab(button.dataset.tab)));
  $('#settingsBtn').addEventListener('click', () => { populateSettings(); $('#settingsDialog').showModal(); renderSettingsPreview(); startSettingsPreviewLoop(); });
  $('#saveSettingsBtn').addEventListener('click', async () => {
    const active = profiles.find(profile => profile.id === state.activeProfileId);
    let saved = false;
    if (active || $('#profileName').value.trim()) saved = await persistCurrentProfile($('#saveSettingsBtn'));
    else {
      $('#saveSettingsBtn').disabled = true;
      try { await saveProjectNow(); setSettingsDirty(false); showSaved($('#saveSettingsBtn'), 'Save changes'); toast('Settings saved for this reel'); saved = true; }
      catch (error) { $('#saveSettingsBtn').disabled = false; $('#saveSettingsBtn').innerHTML = `${ICONS.save}<span>Save changes</span>`; toast('Could not save settings'); }
    }
    if (saved) { refreshVisuals(); $('#settingsDialog').close(); }
  });
  $('#settingsDialog').addEventListener('close', () => cancelAnimationFrame(settingsPreviewRaf));
  $$('.settings-tabs [data-settings-tab]').forEach(button => button.addEventListener('click', () => {
    settingsPreviewTab = button.dataset.settingsTab;
    $$('.settings-tabs [data-settings-tab]').forEach(item => item.classList.toggle('active', item === button));
    $$('[data-settings-panel]').forEach(panel => { const active = panel.dataset.settingsPanel === settingsPreviewTab; panel.classList.toggle('active', active); panel.hidden = !active; });
    renderSettingsPreview(); startSettingsPreviewLoop();
  }));
  $('#qualitySetting').addEventListener('change', () => { state.settings.outputQuality = $('#qualitySetting').value; $('#dialogQualitySetting').value = state.settings.outputQuality; setSettingsDirty(); scheduleSave(); toast(`${OUTPUT_PRESETS[state.settings.outputQuality].label} export selected`); });
  $('#profileSelect').addEventListener('change', handleProfileSelection);
  $('#sidebarProfileSelect').addEventListener('change', handleProfileSelection);
  $('#mobileProfileSelect').addEventListener('change', handleProfileSelection);
  $('#settingsPreviewToggle').addEventListener('click', () => {
    const collapsed = $('#settingsDialog .settings-shell').classList.toggle('preview-collapsed');
    $('#settingsPreviewToggle').setAttribute('aria-label', collapsed ? 'Show settings preview' : 'Hide settings preview');
    $('#settingsPreviewToggle').classList.toggle('active', !collapsed);
  });
  $('#newProfileBtn').addEventListener('click', () => { state.activeProfileId = ''; state.settings = { ...DEFAULT_SETTINGS }; $('#profileName').value = ''; refreshProfileSelects(); populateSettings(); setSettingsDirty(); syncProject(); renderSettingsPreview(); toast('New profile defaults loaded—name and save it'); });
  $('#profileName').addEventListener('input', () => setSettingsDirty());
  $('#saveProfileBtn').addEventListener('click', () => persistCurrentProfile($('#saveProfileBtn')));
  $('#deleteProfileBtn').addEventListener('click', async () => {
    const id = $('#profileSelect').value;
    if (!id) return toast('Select a saved profile first.');
    const profile = profiles.find(item => item.id === id);
    if (!confirm(`Delete ${profile?.name || 'this profile'} from the shared team library?`)) return;
    try {
      const result = await deleteProfile(id);
      profiles = profiles.filter(item => item.id !== id);
      state.activeProfileId = '';
      refreshProfileSelects();
      await saveProjectNow();
      setSettingsDirty(false);
      toast(result.pending ? 'Deletion queued until you are online' : 'Shared profile deleted');
    } catch (error) {
      if (error.status === 409) toast('This profile changed elsewhere. Sync before deleting it.');
      else toast(error.message || 'Could not delete the profile.');
    }
  });
  $('#previewBtn').addEventListener('click', () => openPlayback(0));
  $('#exportBtn').addEventListener('click', exportProject);
  $('#cancelExportBtn').addEventListener('click', () => { $('#cancelExportBtn').disabled = true; $('#cancelExportBtn span').textContent = 'Cancelling…'; exportController?.abort(); window.setTimeout(() => { $('#cancelExportBtn').disabled = false; $('#cancelExportBtn span').textContent = 'Cancel'; }, 500); });
  $('#drawerToggle').addEventListener('click', () => {
    const open = $('#playbackDrawer').classList.toggle('open');
    $('#drawerToggle').setAttribute('aria-label', open ? 'Close live playback' : 'Open live playback');
    if (!open) pausePlayback();
  });
  $('#playPauseBtn').addEventListener('click', () => playing ? pausePlayback() : resumePlayback());
  $('#scrubber').addEventListener('input', () => { playbackTime = Number($('#scrubber').value) / 1000 * playbackDuration(); drawPlayback(playbackTime); if (playing) playbackStart = performance.now() - playbackTime * 1000; });
  $('#installBtn').addEventListener('click', async () => { if (installPrompt) { installPrompt.prompt(); await installPrompt.userChoice; installPrompt = null; $('#installBtn').hidden = true; } });
  window.addEventListener('beforeinstallprompt', (event) => { event.preventDefault(); installPrompt = event; $('#installBtn').hidden = false; });
}

async function initialize() {
  window.addEventListener('reelpro:profile-sync', event => {
    const indicator = $('#profileSyncState');
    if (!indicator) return;
    if (settingsDirty && state.activeProfileId && event.detail?.state === 'synced') return;
    indicator.dataset.state = event.detail?.state || 'offline';
    indicator.textContent = event.detail?.message || 'Profile sync unavailable';
  });
  bindEvents();
  try {
    installedCustomFonts = await registerCustomFonts(await listCustomFonts());
  } catch (error) {
    console.error('Could not restore client fonts', error);
  }
  bindSettings();
  await document.fonts.ready;
  try {
    profiles = (await listProfiles()).map(profile => ({ ...profile, settings: importProfileSettings(profile.settings) }));
    const saved = await loadProject();
    if (saved) {
      Object.assign(state, normalizeProject({ ...saved, images: saved.images.map(hydrateImage), trialImages: (saved.trialImages || []).map(hydrateImage) }));
      $('#scriptInput').value = state.scriptText || JSON.stringify(state.script, null, 2);
      $('#trialScriptInput').value = state.trialScriptText || JSON.stringify(state.trialScript, null, 2);
    }
    const activeProfile = profiles.find(profile => profile.id === state.activeProfileId);
    if (activeProfile) state.settings = importProfileSettings(activeProfile.settings);
    setSettingsDirty(false);
    refreshProfileSelects();
    $('#qualitySetting').value = state.settings.outputQuality || 'hd';
    $('#exportScopeSetting').value = state.settings.exportScope || 'auto';
    const missingFonts = await ensureFontsLoaded([state.settings.heroFont, state.settings.bodyFont, state.settings.subtextFont]);
    if (missingFonts.length) toast(`Missing font: ${missingFonts.join(', ')}`);
    await requestPersistentStorage();
    const estimate = await getStorageEstimate();
    if (estimate?.quota) $('#offlineState').textContent = `${Math.round(estimate.quota / 1024 / 1024)} MB browser storage available`;
    else $('#offlineState').textContent = 'Offline storage ready';
  } catch (error) {
    console.error(error);
    $('#offlineState').textContent = 'Local storage needs permission';
  }
  await refreshVisuals();
  if ('serviceWorker' in navigator && location.protocol !== 'file:') {
    navigator.serviceWorker.register('/sw.js').catch(console.error);
  }
  window.addEventListener('online', () => refreshSharedProfiles());
  window.addEventListener('focus', () => refreshSharedProfiles());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshSharedProfiles(); });
  window.setInterval(() => { if (!document.hidden) refreshSharedProfiles(); }, 15000);
}

initialize();
